/**
 * Tipografia do corpo de leitura (`.article-content`), compartilhada entre a
 * pagina de artigo (`/artigos/[slug]`, onde o HTML vem do painel via
 * dangerouslySetInnerHTML) e a de cases (`/cases`, onde o mesmo markup e
 * montado em JSX a partir do messages/*.json). Manter as duas visualmente
 * identicas depende de ser literalmente a mesma folha de estilo.
 *
 * Tamanhos fixos (sem !important e sem controle A-/A+): a regra anterior
 * aplicava font-size/line-height com !important ao proprio .article-content e a
 * "div", o que vazava por heranca para titulos, tabelas e figuras e atropelava
 * as regras de estrutura abaixo - o artigo saia com titulo do tamanho do corpo
 * e tabela desalinhada. A escala segue o corpo de texto do artigo de
 * referencia (barbieriadvogados.com), com line-height 1.75.
 */
export default function ArticleContentStyles() {
  return (
    <style>{`
      .article-content {
        font-size: clamp(17px, 1.1vw + 14px, 19px);
        line-height: 1.75;
      }
      .article-content h1 { font-size: 2em; }
      .article-content h2 { font-size: 1.5em; }
      .article-content h3 { font-size: 1.2em; }

      /*
        O Preflight do Tailwind zera margin/padding/list-style/border de TODOS
        os elementos, inclusive dentro de um dangerouslySetInnerHTML. Como o
        conteudo do artigo e HTML semantico puro (h2/h3/p/ul/table/strong)
        vindo do painel, sem essas regras ele renderiza como um bloco unico de
        texto corrido: paragrafos colados, titulos do mesmo peso do corpo,
        listas sem marcador e tabelas sem grade. As cores abaixo reproduzem o
        e-book original: navy #0f2740 nos titulos de secao, ciano #1a9fff nos
        subtitulos e cabecalho de tabela navy com texto branco.
      */

      /* SiteHeader e sticky (top:0); sem isso o titulo alvo do link do sumario fica escondido atras do header ao rolar ate a ancora. */
      .article-content h2, .article-content h3 { scroll-margin-top: 96px; }

      .article-content h2 {
        margin: 56px 0 18px;
        font-weight: 700;
        line-height: 1.25 !important;
        color: #0f2740;
        letter-spacing: -0.01em;
        padding-bottom: 12px;
        border-bottom: 2px solid #e8eef5;
      }
      .article-content h2:first-child { margin-top: 0; }
      .article-content h3 {
        margin: 36px 0 12px;
        font-weight: 700;
        line-height: 1.3 !important;
        color: #1a9fff;
      }

      .article-content p { margin: 0 0 20px; }
      .article-content strong { font-weight: 700; color: #0f2740; }
      .article-content em { font-style: italic; }
      .article-content a {
        color: #0a5fb8;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      .article-content a:hover { color: #1a9fff; }

      .article-content ul, .article-content ol {
        margin: 0 0 20px;
        padding-left: 24px;
      }
      .article-content ul { list-style: disc; }
      .article-content ol { list-style: decimal; }
      .article-content li { margin-bottom: 10px; }
      .article-content li::marker { color: #1a9fff; }

      /*
        Tabelas do e-book sao largas (ate 5 colunas). Em telas estreitas elas
        precisam rolar dentro do proprio bloco, senao empurram a largura da
        pagina inteira.
      */
      .article-content table {
        display: block;
        overflow-x: auto;
        width: 100%;
        margin: 28px 0;
        border-collapse: collapse;
        border: 1px solid #d9e2ec;
      }
      /*
        As tabelas do artigo usam <tr><th> direto na primeira linha, sem
        <thead>/<tbody> (mesma convencao do artigo de referencia usado como
        modelo) - "thead { background }" sozinho nunca disparava, entao o
        texto branco do th ficava sem fundo escuro atras: cabecalho
        ilegivel, bug real visto em producao. O fundo precisa estar no
        proprio th. Pelo mesmo motivo, o zebra-striping abaixo usa
        ":nth-child" contando a partir da propria <table> (sem tbody) - a
        linha de cabecalho e a 1a (impar) e fica de fora naturalmente, sem
        precisar de tbody para isso funcionar.
      */
      .article-content th {
        background: #0f2740;
        padding: 12px 14px;
        text-align: left;
        font-weight: 700;
        color: #ffffff;
        border: 1px solid #24405c;
        white-space: nowrap;
      }
      .article-content td {
        padding: 12px 14px;
        vertical-align: top;
        border: 1px solid #d9e2ec;
      }
      .article-content tr:nth-child(even) { background: #f6f9fc; }

      .article-content figure { margin: 28px 0; }
      .article-content figure img { display: block; width: 100%; height: auto; border-radius: 6px; }
      .article-content figcaption {
        margin-top: 8px;
        font-size: 13px !important;
        line-height: 1.5 !important;
        color: #8a8a8a;
        text-align: center;
      }

      /*
        Caixas de destaque do e-book ("Ideia-chave", "Por que GPU?", "Regra de
        ouro"...). No HTML elas sao um <p> que comeca com <strong>rotulo:</strong>,
        entao o seletor :has(> strong:first-child) reconstroi o box azul claro
        do PDF sem precisar mudar o conteudo no painel.
      */
      .article-content p:has(> strong:first-child) {
        margin: 28px 0;
        padding: 18px 22px;
        background: #eef6fd;
        border-left: 4px solid #1a9fff;
        border-radius: 0 6px 6px 0;
      }
      .article-content p:has(> strong:first-child) strong { color: #0f2740; }
    `}</style>
  );
}
